// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { format, parseISO, formatISO, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { useToast } from "../../components/toast/ToastProvider";
import { API } from "../../utils/apiUrls";
import CustomDateRangePicker from "../../utils/CustomDateRangePicker";
import { getCodRemittanceReportStatusName } from "../../utils/enums";

const lottieSrc = "/animations/loader.lottie";

// ───────────────────────────────────────── Helpers
const inr = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n)
    : "-";

// Open a URL in a new tab safely
const openInNewTab = (url) => {
  if (!url) return;
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    console.error("open failed", e);
  }
};

// Fetch presigned URL (auth required) then open it
async function fetchPresignedAndOpen(key, fileName) {
  if (!key || !fileName) return;

  const base =
    typeof API?.getCodRemittanceReportUrl === "function"
      ? API.getCodRemittanceReportUrl()
      : "https://pocas.api.wayuvega.in/v1/cod-remittance/report";

  const url = new URL(base);
  url.searchParams.set("key", key);
  url.searchParams.set("fileName", fileName);

  try {
    const jwt = localStorage.getItem("authToken") || "";
    if (!jwt) throw new Error("Not authenticated");

    const res = await axios.get(url.toString(), {
      headers: {
        Authorization: jwt,
        "Content-Type": "application/json",
      },
    });

    const presigned = res?.data?.presigned_url || res?.data?.presignedUrl;
    if (!presigned) throw new Error("presigned_url missing in response");

    const a = document.createElement("a");
    a.href = presigned;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    console.error("Failed to fetch presigned url", err);
  }
}

const STATUS_MAP = {
  GENERATED: "REPORT_GENERATED",
  REMITTED: "REMITTED",
};

const statusPill = (statusCode) => {
  const name = getCodRemittanceReportStatusName(statusCode);
  let cls = "bg-gray-100 text-gray-700";
  let icon = "⚪";
  if (statusCode === "REMITTED") {
    cls = "bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200";
    icon = "✓";
  } else if (statusCode === "REPORT_GENERATED") {
    cls = "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200";
    icon = "📊";
  }
  return { text: name, cls, icon };
};

const normalizeReport = (r) => {
  // snake & camel compatibility
  const periodStartRaw = r.period_start || r.periodStart;
  const periodEndRaw = r.period_end || r.periodEnd;

  const start = periodStartRaw ? parseISO(periodStartRaw) : null;
  const end = periodEndRaw ? parseISO(periodEndRaw) : null;

  const periodLabel = start && end ? `${format(start, "dd LLL")} - ${format(end, "dd LLL")}` : "-";

  const totalPayable = Number(r.total_cod_amount ?? r.totalCODAmount ?? 0);

  const totalRemitted = Number(r.remitted_cod_amount ?? r.total_cod_amount ?? 0);

  const rawStatus = r.report_status || r.reportStatus || null;

  const details = Array.isArray(r.amount_remittance_details)
    ? r.amount_remittance_details
    : Array.isArray(r.amountRemittanceDetails)
      ? r.amountRemittanceDetails
      : [];

  const summedRemitted = details.reduce((acc, d) => {
    const amt = d?.remitted_amount?.amount ?? d?.remittedAmount?.amount ?? 0;
    return acc + Number(amt || 0);
  }, 0);

  const displayStatus = rawStatus || (summedRemitted > 0 ? "REMITTED" : "REPORT_GENERATED");

  // If REMITTED, show Remitted Amount == COD Amount Payable
  const remittedForDisplay = displayStatus === "REMITTED" ? totalRemitted : summedRemitted || null;

  // Legacy direct URLs (old API)
  const legacyMerchantUrl = r.remittance_report_link?.merchant_csv || null;
  const legacyAdminUrl = r.remittance_report_link?.admin_csv || null;

  // New S3 key + filename (new API – snake & camel)
  const s3 = r.remittance_report_s3_key || r.remittanceReportS3Key || {};
  const merchantKey = s3.merchant_report_s3_key || s3.merchantReportS3Key || null;
  const adminKey = s3.admin_report_s3_key || s3.adminReportS3Key || null;
  const merchantFileName = s3.merchant_report_file_name || s3.merchantReportFileName || null;
  const adminFileName = s3.admin_report_file_name || s3.adminReportFileName || null;

  const createdRaw = r.created_date_time || r.createdDateTime;
  const createdAt = createdRaw ? parseISO(createdRaw) : null;

  return {
    ...r,
    _period_label: periodLabel,
    _period_start: start,
    _period_end: end,
    _display_status: displayStatus,
    _cod_payable: totalPayable,
    _remitted_amount: remittedForDisplay,
    // Download (legacy)
    admin_cod_remittance_report_s3_url: legacyAdminUrl,
    merchant_cod_remittance_report_s3_url: legacyMerchantUrl,
    // Download-by-key (new)
    _admin_report_key: adminKey,
    _admin_report_filename: adminFileName,
    _merchant_report_key: merchantKey,
    _merchant_report_filename: merchantFileName,
    _created_at: createdAt,
    _created_label: createdAt ? format(createdAt, "dd LLL yyyy, HH:mm") : "-",
    _remittance_details: details,
  };
};

// read merchantId from JWT
function readMerchantIdFromJwt(jwt) {
  try {
    const [, payload] = jwt.split(".");
    if (!payload) return null;
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
    return json.merchantId || json.merchant_id || null;
  } catch {
    return null;
  }
}

// ───────────────────────────────────────── Component
export default function CodRemittance() {
  const { showToast } = useToast();

  const today = new Date();

  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  const [range, setRange] = useState([oneMonthAgo, today]);

  const [inputFilters, setInputFilters] = useState({ status: "", search: "" });
  const [activeFilters, setActiveFilters] = useState({ status: "", search: "" });

  const [rows, setRows] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasNext, setHasNext] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    // Default to grid for mobile (< 768px), table for desktop
    return typeof window !== 'undefined' && window.innerWidth < 768 ? "grid" : "table";
  }); // grid or table

  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  const url =
    typeof API?.getCodRemittanceSearch === "function"
      ? API.getCodRemittanceSearch()
      : "/v1/cod-remittance/reports";

  const loadPage = useCallback(
    async (cursorToUse = null) => {
      const isFirstPage = cursorToUse == null;
      isFirstPage ? setLoadingInitial(true) : setLoadingMore(true);
      setError(null);

      try {
        const jwt = localStorage.getItem("authToken") || "";
        if (!jwt) throw new Error("Not authenticated");

        const merchantId = readMerchantIdFromJwt(jwt);
        if (!merchantId) throw new Error("merchantId missing in token");

        const fromDate = new Date(range[0]);
        fromDate.setHours(0, 0, 0, 0);

        const toDate = new Date(range[1]);
        toDate.setHours(23, 59, 59, 999);

        const fromISO = formatISO(fromDate);
        const toISO = formatISO(toDate);

        const body = {
          merchant_id: [String(merchantId)],
          from_period_start: fromISO,
          to_period_end: toISO,
          ...(activeFilters.status && { cod_remittance_report_status_list: [STATUS_MAP[activeFilters.status]] }),
          limit: 50,
          cursor: cursorToUse,
          sort_by: "createdDateTime",
          sort_direction: "DESC",
        };

        const res = await axios.post(url, body, {
          headers: {
            Authorization: jwt,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        const list = Array.isArray(res.data?.items)
          ? res.data.items
          : Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data)
              ? res.data
              : [];

        const pageInfo = res.data?.page || {};
        const newNext = pageInfo?.next_cursor ?? null;
        const newHasNext = !!pageInfo?.has_next;

        const normalized = list.map(normalizeReport);

        setRows((prev) => (isFirstPage ? normalized : [...prev, ...normalized]));
        setNextCursor(newNext);
        setHasNext(newHasNext);
      } catch (e) {
        console.error(e);
        setError(e?.response?.data?.message || e.message || "Failed to fetch reports");
      } finally {
        isFirstPage ? setLoadingInitial(false) : setLoadingMore(false);
      }
    },
    [range, activeFilters, url]
  );

  const resetAndLoad = useCallback(() => {
    setRows([]);
    setNextCursor(null);
    setHasNext(false);
    loadPage(null);
  }, [loadPage]);

  useEffect(() => {
    resetAndLoad();
  }, [resetAndLoad]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (loadingInitial || loadingMore) return;
        if (!hasNext) return;
        loadPage(nextCursor);
      },
      { root: null, rootMargin: "0px 0px 200px 0px", threshold: 0.1 }
    );

    const current = sentinelRef.current;
    if (current) observerRef.current.observe(current);

    return () => observerRef.current && observerRef.current.disconnect();
  }, [hasNext, nextCursor, loadingInitial, loadingMore, loadPage]);

  const filteredRows = useMemo(() => {
    if (!rows?.length) return [];
    let result = rows;
    
    // Status filter
    if (activeFilters.status) {
      const apiEnum = STATUS_MAP[activeFilters.status];
      result = result.filter((r) => r._display_status === apiEnum);
    }
    
    // Search filter
    if (activeFilters.search) {
      const search = activeFilters.search.toLowerCase();
      result = result.filter((r) => {
        const reportId = (r.report_id || r.reportId || "").toLowerCase();
        const periodLabel = (r._period_label || "").toLowerCase();
        const awbs = (r.awbs || []).join(" ").toLowerCase();
        return reportId.includes(search) || periodLabel.includes(search) || awbs.includes(search);
      });
    }
    
    return result;
  }, [rows, activeFilters]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (!filteredRows.length) {
      return {
        totalReports: 0,
        totalCODAmount: 0,
        totalRemitted: 0,
        pendingRemittance: 0,
        totalOrders: 0,
      };
    }

    const totalReports = filteredRows.length;
    const totalCODAmount = filteredRows.reduce((sum, r) => sum + (r._cod_payable || 0), 0);
    const totalRemitted = filteredRows
      .filter((r) => r._display_status === "REMITTED")
      .reduce((sum, r) => sum + (r._remitted_amount || 0), 0);
    const pendingRemittance = totalCODAmount - totalRemitted;
    const totalOrders = filteredRows.reduce((sum, r) => sum + (r.order_count || r.orderCount || 0), 0);

    return {
      totalReports,
      totalCODAmount,
      totalRemitted,
      pendingRemittance,
      totalOrders,
    };
  }, [filteredRows]);

  const applyFilters = () => setActiveFilters(inputFilters);
  const resetFilters = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setRange([start, now]);
    setInputFilters({ status: "", search: "" });
    setActiveFilters({ status: "", search: "" });
  };
  
  const setQuickRange = (preset) => {
    const now = new Date();
    let start;
    switch (preset) {
      case "today":
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        break;
      case "7days":
        start = subDays(now, 7);
        break;
      case "30days":
        start = subDays(now, 30);
        break;
      case "thisMonth":
        start = startOfMonth(now);
        break;
      case "lastMonth":
        start = startOfMonth(subMonths(now, 1));
        now.setTime(endOfMonth(subMonths(now, 1)).getTime());
        break;
      default:
        return;
    }
    setRange([start, now]);
  };
  
  const openDetails = (row) => {
    setDetailsRow(row);
    setDetailsOpen(true);
  };

  const exportToCSV = () => {
    if (!filteredRows.length) {
      return showToast("No data to export", "warning");
    }
    
    const headers = ["Period", "Report Generated", "Total Orders", "Status", "COD Amount", "Remitted Amount"];
    const csvRows = [headers.join(",")];
    
    filteredRows.forEach((r) => {
      const row = [
        r._period_label,
        r._created_label,
        r.order_count ?? r.orderCount ?? 0,
        statusPill(r._display_status).text,
        r._cod_payable || 0,
        r._remitted_amount || 0,
      ];
      csvRows.push(row.join(","));
    });
    
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `COD_Remittance_${format(new Date(), "yyyy-MM-dd")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showToast("Export successful", "success");
  };

  // ───────────────────────────────────────── Render
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 pt-2 sm:pt-24 lg:pt-2 px-4 sm:px-6 lg:px-2 pb-8">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-xl md:text-xl lg:text-xl font-bold text-gray-900 flex items-center gap-2 md:gap-3">COD Remittance</h3>
              <p className="mt-1 text-sm text-gray-500">Manage and track your cash on delivery remittances</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {showFilters ? "Hide" : "Show"} Filters
              </button>
            </div>
          </div>
        </div>

        {/* FILTER BAR */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-2 transition-all duration-300">
            <div className="space-y-6">
              {/* Quick Date Presets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quick Select</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "Today", value: "today" },
                    { label: "Last 7 Days", value: "7days" },
                    { label: "Last 30 Days", value: "30days" },
                    { label: "This Month", value: "thisMonth" },
                    { label: "Last Month", value: "lastMonth" },
                  ].map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setQuickRange(preset.value)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Period Range</label>
                  <CustomDateRangePicker selectedRange={range} onRangeChange={setRange} label="Period" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    name="status"
                    value={inputFilters.status}
                    onChange={(e) => setInputFilters({ ...inputFilters, status: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="GENERATED">Report Generated</option>
                    <option value="REMITTED">Remitted</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by report ID, AWB..."
                      value={inputFilters.search}
                      onChange={(e) => setInputFilters({ ...inputFilters, search: e.target.value })}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                <div className="flex items-end gap-2">
                  <button
                    onClick={applyFilters}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Apply
                  </button>
                  <button
                    onClick={resetFilters}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Active Filters */}
              {(activeFilters.status || activeFilters.search) && (
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Active Filters:</span>
                  {activeFilters.status && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      Status: {activeFilters.status}
                      <button onClick={() => setActiveFilters({ ...activeFilters, status: "" })} className="ml-1">×</button>
                    </span>
                  )}
                  {activeFilters.search && (
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      Search: {activeFilters.search}
                      <button onClick={() => setActiveFilters({ ...activeFilters, search: "" })} className="ml-1">×</button>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Reports</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalReports}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalOrders}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total COD</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{inr(stats.totalCODAmount)}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE/GRID TOGGLE */}
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm text-gray-600">
            Showing <span className="font-medium text-gray-900">{filteredRows.length}</span> report{filteredRows.length !== 1 ? "s" : ""}
          </p>
          <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 ${
                viewMode === "table" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 ${
                viewMode === "grid" ? "bg-blue-600 text-white shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M4 5h3v3H4V5zm0 5h3v3H4v-3zm0 5h3v3H4v-3zm5-10h11v3H9V5zm0 5h11v3H9v-3zm0 5h11v3H9v-3z" />
              </svg>
            </button>
          </div>
        </div>
        {/* CONTENT AREA */}
        {loadingInitial && rows.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <DotLottieReact src={lottieSrc} loop autoplay style={{ width: 120, height: 120 }} />
              <p className="mt-4 text-gray-600 font-medium">Loading remittance data...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8">
            <div className="flex items-center gap-3 text-red-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="font-medium">{error}</p>
            </div>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No remittance reports found</h3>
              <p className="text-gray-500 max-w-md">Try adjusting your filters or date range to see more results.</p>
            </div>
          </div>
        ) : viewMode === "grid" ? (
          /* GRID VIEW */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredRows.map((r) => {
              const pill = statusPill(r._display_status);
              const onDownloadAdmin = () => {
                if (r.admin_cod_remittance_report_s3_url) {
                  openInNewTab(r.admin_cod_remittance_report_s3_url);
                  return;
                }
                if (!r._admin_report_key || !r._admin_report_filename) {
                  return showToast("Admin report not available", "warning");
                }
                fetchPresignedAndOpen(r._admin_report_key, r._admin_report_filename);
              };
              const onDownloadMerchant = () => {
                if (r.merchant_cod_remittance_report_s3_url) {
                  openInNewTab(r.merchant_cod_remittance_report_s3_url);
                  return;
                }
                if (!r._merchant_report_key || !r._merchant_report_filename) {
                  return showToast("Merchant report not available", "warning");
                }
                fetchPresignedAndOpen(r._merchant_report_key, r._merchant_report_filename);
              };

              return (
                <div
                  key={r.report_id || r.reportId}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer"
                  onClick={() => openDetails(r)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{r._period_label}</h3>
                      <p className="text-sm text-gray-500 mt-1">{r._created_label}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${pill.cls}`}>
                      <span>{pill.icon}</span>
                      {pill.text}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Total Orders</p>
                      <p className="text-lg font-bold text-gray-900">{r.order_count ?? r.orderCount ?? "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">COD Amount</p>
                      <p className="text-lg font-bold text-gray-900">{inr(r._cod_payable)}</p>
                    </div>
                    {r._display_status === "REMITTED" && (
                      <>
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500 mb-1">Remitted Amount</p>
                          <p className="text-lg font-bold text-green-600">{r._remitted_amount != null ? inr(r._remitted_amount) : "-"}</p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-100 flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownloadAdmin();
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Admin
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownloadMerchant();
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Merchant
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetails(r);
                      }}
                      className="px-3 py-2 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* TABLE VIEW */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                  <tr>
                    {["Period", "Report Generated", "Orders", "Status", "COD Amount", "Remitted", "Actions"].map((h) => (
                      <th key={h} className="px-6 py-4 text-left text-sm font-semibold tracking-wide whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRows.map((r, idx) => {
                    const pill = statusPill(r._display_status);
                    const onDownloadAdmin = () => {
                      if (r.admin_cod_remittance_report_s3_url) {
                        openInNewTab(r.admin_cod_remittance_report_s3_url);
                        return;
                      }
                      if (!r._admin_report_key || !r._admin_report_filename) {
                        return showToast("Admin report not available", "warning");
                      }
                      fetchPresignedAndOpen(r._admin_report_key, r._admin_report_filename);
                    };
                    const onDownloadMerchant = () => {
                      if (r.merchant_cod_remittance_report_s3_url) {
                        openInNewTab(r.merchant_cod_remittance_report_s3_url);
                        return;
                      }
                      if (!r._merchant_report_key || !r._merchant_report_filename) {
                        return showToast("Merchant report not available", "warning");
                      }
                      fetchPresignedAndOpen(r._merchant_report_key, r._merchant_report_filename);
                    };

                    return (
                      <tr
                        key={r.report_id || r.reportId}
                        className={`hover:bg-blue-50 transition-colors duration-150 cursor-pointer ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                        onClick={() => openDetails(r)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{r._period_label}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">{r._created_label}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">{r.order_count ?? r.orderCount ?? "-"}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${pill.cls}`}>
                            <span>{pill.icon}</span>
                            {pill.text}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">{inr(r._cod_payable)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-green-600">
                            {r._display_status === "REMITTED" && r._remitted_amount != null ? inr(r._remitted_amount) : "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownloadAdmin();
                              }}
                              title="Download Admin Report"
                              className="p-2 hover:bg-blue-100 rounded-lg transition-colors duration-200 group"
                            >
                              <svg className="w-5 h-5 text-gray-600 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownloadMerchant();
                              }}
                              title="Download Merchant Report"
                              className="p-2 hover:bg-indigo-100 rounded-lg transition-colors duration-200 group"
                            >
                              <svg className="w-5 h-5 text-gray-600 group-hover:text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetails(r);
                              }}
                              title="View Details"
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 group"
                            >
                              <svg className="w-5 h-5 text-gray-600 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-4" />
            {loadingMore && (
              <div className="py-8 flex justify-center border-t border-gray-200">
                <DotLottieReact src={lottieSrc} loop autoplay style={{ width: 60, height: 60 }} />
              </div>
            )}
          </div>
        )}

      {/* DETAILS MODAL */}
      {detailsOpen && detailsRow && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-slideUp">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Remittance Details</h2>
                  <p className="text-blue-100 mt-1 text-sm">{detailsRow._period_label}</p>
                </div>
                <button
                  onClick={() => setDetailsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Status Badge */}
              <div className="mb-6">
                {(() => {
                  const pill = statusPill(detailsRow._display_status);
                  return (
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${pill.cls}`}>
                      <span className="text-lg">{pill.icon}</span>
                      {pill.text}
                    </span>
                  );
                })()}
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700 font-medium">Report ID</p>
                      <p className="text-sm font-bold text-blue-900 truncate">{(detailsRow.report_id || detailsRow.reportId || "-").slice(0, 8)}...</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500 rounded-lg">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700 font-medium">Total Orders</p>
                      <p className="text-sm font-bold text-purple-900">{detailsRow.order_count ?? detailsRow.orderCount ?? "-"}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500 rounded-lg">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-amber-700 font-medium">Total COD</p>
                      <p className="text-sm font-bold text-amber-900">{inr(detailsRow._cod_payable)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-green-700 font-medium">Remitted</p>
                      <p className="text-sm font-bold text-green-900">
                        {detailsRow._remitted_amount != null ? inr(detailsRow._remitted_amount) : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* AWBs Section */}
              {detailsRow.awbs && detailsRow.awbs.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-5 mb-6 border border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h3 className="text-base font-semibold text-gray-900">AWB Numbers ({detailsRow.awbs.length})</h3>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {detailsRow.awbs.map((awb) => (
                        <span key={awb} className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200">
                          {awb}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Remittance Details Table */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Remittance Transactions
                  </h3>
                </div>
                {Array.isArray(detailsRow._remittance_details) && detailsRow._remittance_details.length > 0 ? (
                  <div className="overflow-x-auto max-h-80">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Mode</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Reference</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Account</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {detailsRow._remittance_details.map((d, i) => {
                          const amt = d?.remitted_amount?.amount ?? d?.remittedAmount?.amount ?? null;
                          const cur = d?.remitted_amount?.currency ?? d?.remittedAmount?.currency ?? "";
                          return (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {d?.mode_of_payment ?? d?.modeOfPayment ?? "-"}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-900 font-medium">{d?.payment_reference_number ?? d?.paymentReferenceNumber ?? "-"}</td>
                              <td className="px-4 py-3 text-gray-900 font-bold">
                                {amt != null ? inr(Number(amt)) : "-"} {cur && cur !== "INR" ? `(${cur})` : ""}
                              </td>
                              <td className="px-4 py-3 text-gray-700">{d?.destination_account_number ?? d?.destinationAccountNumber ?? "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-8 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-sm font-medium">No remittance transactions recorded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    if (detailsRow.admin_cod_remittance_report_s3_url) {
                      return openInNewTab(detailsRow.admin_cod_remittance_report_s3_url);
                    }
                    if (!detailsRow._admin_report_key || !detailsRow._admin_report_filename) {
                      return showToast("Admin report not available", "warning");
                    }
                    fetchPresignedAndOpen(detailsRow._admin_report_key, detailsRow._admin_report_filename);
                  }}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-2.5 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Admin CSV
                </button>
                <button
                  onClick={() => {
                    if (detailsRow.merchant_cod_remittance_report_s3_url) {
                      return openInNewTab(detailsRow.merchant_cod_remittance_report_s3_url);
                    }
                    if (!detailsRow._merchant_report_key || !detailsRow._merchant_report_filename) {
                      return showToast("Merchant report not available", "warning");
                    }
                    fetchPresignedAndOpen(detailsRow._merchant_report_key, detailsRow._merchant_report_filename);
                  }}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Merchant CSV
                </button>
              </div>
              <button
                onClick={() => setDetailsOpen(false)}
                className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}